import axios from 'axios';
import { Calendar, CheckSquare, Target, Heart } from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// Retry helper: 2 attempts total with exponential backoff for transient
// errors (5xx, 520, network).
// ─────────────────────────────────────────────────────────────
const RETRY_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524]);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const axiosGetWithRetry = async (url, options = {}, maxRetries = 2) => {
  let lastErr = null;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await axios.get(url, options);
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      const isRetriable = !status || RETRY_STATUSES.has(status);
      if (!isRetriable || attempt === maxRetries) throw err;
      await sleep(400 * Math.pow(2, attempt)); // 400ms, 800ms
    }
  }
  throw lastErr;
};

// ─────────────────────────────────────────────────────────────
// Merge committed items (real appointments from /operational.agenda) +
// proposed contacts (from /weekly-plan.distribution) per day.
// Returns { committed: [...], proposal: [...] } capped by rules.
// ─────────────────────────────────────────────────────────────
export const MAX_TOTAL_PER_DAY = 6;

export const buildCommittedProposal = (date, operationalAgenda, planItems) => {
  const dayOp = (operationalAgenda || []).find((d) => d.date === date) || {};
  const committed = [];
  (dayOp.tasks || []).forEach((t) => committed.push({
    __kind: 'task', item_type: 'task', item_id: t.id, title: t.title,
    scope: t.scope, priority: t.priority, is_overdue: t.is_overdue, days_overdue: t.days_overdue,
    company_id: t.company_id, opportunity_id: t.opportunity_id,
  }));
  (dayOp.activities || []).forEach((a) => committed.push({
    __kind: 'activity', item_type: 'activity', item_id: a.id, title: a.title,
    scope: a.scope, time: a.time, type: a.type, location: a.location,
    company_id: a.company_id, contact_id: a.contact_id, opportunity_id: a.opportunity_id,
  }));
  (dayOp.deadlines || []).forEach((d) => committed.push({
    __kind: 'deadline', item_type: 'urgent_opportunity', item_id: d.id, title: d.title,
    scope: d.scope, stage: d.stage, company_id: d.company_id,
  }));
  // Also treat weekly-plan urgent_opportunity items as committed
  (planItems || []).filter((i) => i.item_type === 'urgent_opportunity').forEach((i) => {
    if (!committed.find((c) => c.item_id === i.item_id && c.item_type === i.item_type)) {
      committed.push({ ...i, __kind: 'urgent_opp' });
    }
  });
  // Sort: overdue first, then by time, then by kind (activity → task → deadline)
  const kindOrder = { activity: 0, urgent_opp: 1, deadline: 2, task: 3 };
  committed.sort((a, b) => {
    if (!!a.is_overdue !== !!b.is_overdue) return a.is_overdue ? -1 : 1;
    if (a.time && b.time) return String(a.time).localeCompare(String(b.time));
    if (a.time && !b.time) return -1;
    if (!a.time && b.time) return 1;
    return (kindOrder[a.__kind] ?? 9) - (kindOrder[b.__kind] ?? 9);
  });

  // Proposal: contact_cultivation only, max 3, further reduced by total load
  let proposal = (planItems || []).filter((i) => i.item_type === 'contact_cultivation');
  const committedLen = committed.length;
  let maxProposal = 3;
  if (committedLen >= 6) maxProposal = 0;
  else if (committedLen >= 4) maxProposal = 1;
  proposal = proposal.slice(0, maxProposal);
  return { committed, proposal, totalCount: committedLen + proposal.length, maxTotal: MAX_TOTAL_PER_DAY };
};

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────
export const ES_DAY_LABEL = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie'];

export const ITEM_TYPE_LABEL = {
  task: 'Tarea',
  activity: 'Actividad',
  contact_cultivation: 'Cultivar',
  urgent_opportunity: 'Oportunidad',
};

export const ITEM_TYPE_ICON = {
  task: CheckSquare,
  activity: Calendar,
  contact_cultivation: Heart,
  urgent_opportunity: Target,
};

export const PRIORITY_COLOR = {
  high: 'text-orange-600',
  medium: 'text-blue-500',
  low: 'text-slate-400',
};

export const PRIORITY_BG = {
  high: 'bg-orange-50 border-orange-200',
  medium: 'bg-blue-50 border-blue-200',
  low: 'bg-slate-50 border-slate-200',
};

// ─────────────────────────────────────────────────────────────
// Formatters and small utilities (delegated to shared /utils/money.js
// so the whole app uses one euro format: "123.431 €").
// ─────────────────────────────────────────────────────────────
export { formatEuro, formatEuroShort } from '../../utils/money';

export const getItemRoute = (it) => {
  switch (it.item_type) {
    case 'task': return `/tasks?id=${it.item_id}`;
    case 'activity': return `/activities?id=${it.item_id}`;
    case 'contact_cultivation': return `/contacts/${it.item_id}`;
    case 'urgent_opportunity': return `/pipeline/${it.item_id}`;
    default: return '/';
  }
};

export const isItemDueToday = (it, todayStr) => {
  if (it.item_type === 'task' && it.due_date) return (it.due_date.slice(0, 10) === todayStr);
  if (it.item_type === 'activity' && it.date) return (it.date.slice(0, 10) === todayStr);
  return false;
};
